// frontend/src/features/meetings/api/meetingsApi.js
import axiosInstance from '../../../api/axios';

const ROLE_LABELS = {
  org_owner: 'Owner',
  org_admin: 'Admin',
  org_sales_manager: 'Sales Manager',
  org_sales_member: 'Sales Member',
  org_researcher: 'Researcher',
  org_viewer: 'Viewer',
  org_member: 'Member',
  sales_manager: 'Sales Manager',
  sales_member: 'Sales Member',
  sales_rep: 'Sales Rep',
  sales_lead: 'Sales Lead',
  account_executive: 'Account Executive',
  viewer: 'Viewer',
  owner: 'Owner',
  admin: 'Admin',
  member: 'Member',
};

export const formatRoleLabel = (rawRole = '') => {
  if (!rawRole) return 'Member';
  const clean = String(rawRole).trim().toLowerCase();
  if (ROLE_LABELS[clean]) return ROLE_LABELS[clean];

  return (
    clean
      .replace(/^org_/, '')
      .split(/[_\-\s/]+/)
      .filter(Boolean)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ') || 'Member'
  );
};

export const getInitials = (name) => {
  if (!name) return 'TM';
  return (
    name
      .split(/[\s@._-]+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0].toUpperCase())
      .join('') || 'TM'
  );
};

export const formatWorkspaceMemberAsHost = (m) => {
  if (!m) return null;
  const id = m.user_id || m.id || m.userId;
  const name = m.display_name || m.name || m.username || m.email || 'Workspace Member';
  const email = m.email || '';
  const rawRole = m.workspace_role || m.role || m.displayRole || '';
  const role = formatRoleLabel(rawRole);

  return {
    id,
    userId: id,
    username: m.username || '',
    name,
    email,
    rawRole,
    role,
    avatarUrl: m.avatar_url || m.avatarUrl || null,
    avatar: m.avatar || getInitials(name),
    isActive: m.is_active !== false,
  };
};

export const isMeetingPast = (meeting) => {
  if (!meeting) return false;
  const start = meeting.startTime ? new Date(meeting.startTime).getTime() : 0;
  const duration = Number(meeting.durationMinutes) || 30;
  const end = meeting.endTime
    ? new Date(meeting.endTime).getTime()
    : start
      ? start + duration * 60000
      : 0;
  if (!end || isNaN(end)) return false;
  return end <= Date.now();
};

export const isMeetingInProgress = (meeting) => {
  if (!meeting || meeting.status === 'cancelled' || meeting.status === 'completed') return false;
  const start = meeting.startTime ? new Date(meeting.startTime).getTime() : 0;
  if (!start || isNaN(start)) return false;
  const duration = Number(meeting.durationMinutes) || 30;
  const end = meeting.endTime ? new Date(meeting.endTime).getTime() : start + duration * 60000;
  if (!end || isNaN(end)) return false;
  const now = Date.now();
  return start <= now && now < end;
};

export const canJoinMeeting = (meeting, nowMs = Date.now()) => {
  if (!meeting || meeting.status === 'cancelled' || meeting.status === 'completed') return false;
  if (isMeetingPast(meeting)) return false;
  const start = meeting.startTime ? new Date(meeting.startTime).getTime() : 0;
  if (!start || isNaN(start)) return false;
  const fifteenMinsBefore = start - 15 * 60 * 1000;
  return nowMs >= fifteenMinsBefore;
};

export const formatMeetingCountdown = (ms) => {
  if (!ms || ms <= 0) return '0s';
  const totalSeconds = Math.floor(ms / 1000);
  const totalMinutes = Math.floor(totalSeconds / 60);
  const totalHours = Math.floor(totalMinutes / 60);
  const days = Math.floor(totalHours / 24);
  const remainingHours = totalHours % 24;
  const remainingMinutes = totalMinutes % 60;
  const remainingSeconds = totalSeconds % 60;

  if (days > 0) {
    return `${days}d ${remainingHours}h`;
  }
  if (totalHours > 0) {
    return `${totalHours}h ${remainingMinutes}m`;
  }
  if (totalMinutes > 0) {
    return `${totalMinutes}m ${remainingSeconds}s`;
  }
  return `${remainingSeconds}s`;
};

export const getMsUntilMeetingAccessOpen = (meeting, nowMs = Date.now()) => {
  if (!meeting || !meeting.startTime) return 0;
  const start = new Date(meeting.startTime).getTime();
  if (!start || isNaN(start)) return 0;
  const openTime = start - 15 * 60 * 1000;
  return Math.max(0, openTime - nowMs);
};

export const getEffectiveMeetingStatus = (meeting) => {
  if (!meeting) return 'confirmed';
  if (meeting.status === 'cancelled') return 'cancelled';
  if (meeting.status === 'completed') return 'completed';
  if (isMeetingPast(meeting)) {
    return 'completed';
  }
  if (isMeetingInProgress(meeting)) {
    return 'in_progress';
  }
  return meeting.status || 'confirmed';
};

/**
 * Meetings & Calendar Database API Client
 */
export const meetingsApi = {
  /**
   * Fetch meetings for workspace from database
   */
  async getMeetings(workspaceId, params = {}) {
    try {
      const response = await axiosInstance.get(`/workspace/${workspaceId}/meetings`, { params });
      return response.data?.meetings || [];
    } catch (error) {
      console.warn('Failed to fetch meetings from database:', error?.message);
      return null;
    }
  },

  /**
   * Fetch a single meeting by ID
   */
  async getMeetingById(workspaceId, meetingId) {
    try {
      const response = await axiosInstance.get(`/workspace/${workspaceId}/meetings/${meetingId}`);
      return response.data?.meeting || null;
    } catch (error) {
      console.warn(`Failed to fetch meeting ${meetingId}:`, error?.message);
      return null;
    }
  },

  /**
   * Create a new scheduled meeting in database
   */
  async createMeeting(workspaceId, meetingData) {
    try {
      const response = await axiosInstance.post(`/workspace/${workspaceId}/meetings`, meetingData);
      return response.data?.meeting || response.data;
    } catch (error) {
      console.error('Failed to create meeting in database:', error?.message);
      throw error;
    }
  },

  /**
   * Update an existing meeting
   */
  async updateMeeting(workspaceId, meetingId, updateData) {
    try {
      const response = await axiosInstance.patch(
        `/workspace/${workspaceId}/meetings/${meetingId}`,
        updateData
      );
      return response.data?.meeting || response.data;
    } catch (error) {
      console.error(`Failed to update meeting ${meetingId}:`, error?.message);
      throw error;
    }
  },

  /**
   * Delete / Cancel a meeting
   */
  async deleteMeeting(workspaceId, meetingId) {
    try {
      const response = await axiosInstance.delete(
        `/workspace/${workspaceId}/meetings/${meetingId}`
      );
      return response.data;
    } catch (error) {
      console.error(`Failed to delete meeting ${meetingId}:`, error?.message);
      throw error;
    }
  },

  /**
   * Save post-meeting recap
   */
  async saveMeetingRecap(workspaceId, meetingId, recapData) {
    try {
      const response = await axiosInstance.post(
        `/workspace/${workspaceId}/meetings/${meetingId}/recap`,
        recapData
      );
      return response.data?.meeting || response.data;
    } catch (error) {
      console.error(`Failed to save recap for meeting ${meetingId}:`, error?.message);
      throw error;
    }
  },

  /**
   * Fetch meeting types for workspace
   */
  async getMeetingTypes(workspaceId) {
    try {
      const response = await axiosInstance.get(`/workspace/${workspaceId}/meetings/types`);
      return response.data?.meetingTypes || [];
    } catch (error) {
      console.warn('Failed to fetch meeting types from database:', error?.message);
      return null;
    }
  },

  /**
   * Save / Create meeting type
   */
  async saveMeetingType(workspaceId, typeData) {
    try {
      const response = await axiosInstance.post(
        `/workspace/${workspaceId}/meetings/types`,
        typeData
      );
      return response.data?.meetingType || response.data;
    } catch (error) {
      console.error('Failed to save meeting type:', error?.message);
      throw error;
    }
  },

  /**
   * Fetch availability settings and connected calendar states from database
   */
  async getAvailabilitySettings(workspaceId, hostEmail = '', userId = '') {
    try {
      const response = await axiosInstance.get(`/workspace/${workspaceId}/meetings/availability`, {
        params: { hostEmail, userId },
      });
      return response.data?.settings || null;
    } catch (error) {
      console.warn('Failed to fetch availability settings from database:', error?.message);
      return null;
    }
  },

  /**
   * Update availability settings in database
   */
  async updateAvailabilitySettings(workspaceId, settings) {
    try {
      const response = await axiosInstance.put(
        `/workspace/${workspaceId}/meetings/availability`,
        settings
      );
      return response.data?.settings || response.data;
    } catch (error) {
      console.error('Failed to update availability settings in database:', error?.message);
      throw error;
    }
  },

  /**
   * Fetch dynamic workspace host members from workspace membership
   */
  async getWorkspaceHosts(workspaceId) {
    if (!workspaceId) return [];
    try {
      // 1. Fetch from dynamic workspace members endpoint (matching CRM)
      const response = await axiosInstance.get(`/workspace/${workspaceId}/members`);
      const members = response.data?.members || [];
      if (Array.isArray(members) && members.length > 0) {
        return members
          .filter((m) => (m.user_id || m.id) && m.is_active !== false)
          .map(formatWorkspaceMemberAsHost);
      }
    } catch (error) {
      console.warn('Failed to fetch workspace members for meetings:', error?.message);
    }

    // 2. Fallback to meetings hosts route if members route is unavailable
    try {
      const fallbackRes = await axiosInstance.get(`/workspace/${workspaceId}/meetings/hosts`);
      const hosts = fallbackRes.data?.hosts || [];
      if (Array.isArray(hosts) && hosts.length > 0) {
        return hosts.map(formatWorkspaceMemberAsHost);
      }
    } catch (fallbackErr) {
      // ignore
    }

    return [];
  },

  /**
   * Fetch campaigns for workspace to allow import into meeting schedule
   */
  async getWorkspaceCampaigns(workspaceId) {
    try {
      const response = await axiosInstance.get('/campaign', {
        headers: workspaceId ? { 'x-workspace-id': workspaceId } : {},
      });
      return response.data?.data || response.data?.campaigns || [];
    } catch (error) {
      console.warn('Failed to fetch workspace campaigns for meetings:', error?.message);
      return [];
    }
  },

  /**
   * Fetch pipeline leads for workspace to allow import into meeting schedule
   */
  async getPipelineLeads(workspaceId) {
    try {
      const response = await axiosInstance.get('/lead-pipeline', {
        headers: workspaceId ? { 'x-workspace-id': workspaceId } : {},
      });
      return response.data?.data || response.data?.leads || [];
    } catch (error) {
      console.warn('Failed to fetch pipeline leads for meetings:', error?.message);
      return [];
    }
  },

  /**
   * Request real-time LLM-powered AI Brief for a group meeting grounded in database campaign data
   */
  async generateMeetingAiBrief(workspaceId, payload = {}, meetingId = null) {
    try {
      const activeWs = workspaceId || localStorage.getItem('activeWorkspaceId') || '';
      const url = activeWs
        ? meetingId
          ? `/workspace/${activeWs}/meetings/${meetingId}/ai-brief`
          : `/workspace/${activeWs}/meetings/ai-brief`
        : meetingId
          ? `/meetings/${meetingId}/ai-brief`
          : '/meetings/ai-brief';
      const response = await axiosInstance.post(
        url,
        { ...payload, workspaceId: activeWs || undefined },
        {
          headers: activeWs ? { 'x-workspace-id': activeWs } : {},
        }
      );
      return response.data?.data || response.data;
    } catch (error) {
      console.error('Failed to generate AI brief for meeting:', error?.message);
      throw error;
    }
  },

  /**
   * Public booking submission from external booking link
   */
  async bookPublicMeeting(meetingData) {
    try {
      const response = await axiosInstance.post('/meetings/book', meetingData);
      return response.data?.meeting || response.data;
    } catch (error) {
      console.error('Failed to book public meeting:', error?.message);
      throw error;
    }
  },
};

export default meetingsApi;
